/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */
import { FC } from "react";
import { Routes, Route, BrowserRouter, Navigate, useLocation } from "react-router-dom";
import { PrivateRoutes } from "./PrivateRoutes";
import { ErrorsPage } from "../modules/errors/ErrorsPage";
import { Logout, AuthPage, useAuth } from "../modules/auth";
import { App } from "../App";
import { CheckPayment } from "../modules/payment/components/CheckPayment";
import { CheckPackagePayment } from "../modules/payment/components/CheckPackagePayment";
import { EmptyLayout } from "../modules/global/components/EmptyLayout";
import InvitationCheck from "../modules/producerCenter/components/InvitationCheck";

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env;

const NavigatePreservingSearch = ({ to }: { to: string }) => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} />;
};

const AppRoutes: FC = () => {
  const { currentUser } = useAuth();
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path="error/*" element={<ErrorsPage />} />
          <Route path="logout" element={<Logout />} />
          {currentUser ? (
            <>
              <Route path="/*" element={<PrivateRoutes />} />
              <Route index element={<Navigate to="/dashboard" />} />
              <Route element={<EmptyLayout />}>
                <Route path="payment/checkPayment" element={<CheckPayment />} />
                <Route path="payment/checkPackagePayment" element={<CheckPackagePayment />} />
                <Route
                  path="auth/verify-phonenumber"
                  element={<InvitationCheck />}
                />
              </Route>
            </>
          ) : (
            <>
              <Route path="auth/*" element={<AuthPage />} />
              <Route path="*" element={<NavigatePreservingSearch to="/auth" />} />
              <Route element={<EmptyLayout />}>
                <Route path="payment/checkPayment" element={<CheckPayment />} />
                <Route path="payment/checkPackagePayment" element={<CheckPackagePayment />} />
              </Route>
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export { AppRoutes };
